import { templateCrudConfig, type Template } from '@/config/objects/templates';
import { CrudListView, type CrudConfig, type CrudListHeaderAction } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface TemplatesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<Template>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (template: Template) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /templates
   */
  baseUrl?: string;
  /**
   * Custom header actions (displayed in the header alongside Add/Create buttons)
   */
  headerActions?: CrudListHeaderAction[];
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
}

/**
 * Reusable templates list component
 * Pass a custom config with modified actions to customize behavior
 */
export function TemplatesList({
  config,
  baseUrl = '/templates',
  defaultWhere,
  onItemClick,
  className,
  headerActions,
  breadcrumbs,
}: TemplatesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (template: Template) => {
      if (onItemClick) {
        onItemClick(template);
      } else {
        navigate(`/templates/${template.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || templateCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        headerActions={headerActions}
        breadcrumbs={breadcrumbs}
        getValueFromItem={(template: Template) => template.id}
        getLabelFromItem={(template: Template) => template.name}
      />
    </div>
  );
}
